import firebase from 'firebase/compat/app';
import { db } from './config';
import {
  LeadCompetitionData,
  LeadAthlete,
  LeadGroup,
  LeadClass,
  LeadScore,
  getDefaultData,
  scoreKey,
  RouteId,
} from '../utils/leadCompetition';

/** New subcollection path: competitions/{competitionId}/lead/config */
function docRef(competitionId: string): firebase.firestore.DocumentReference {
  return db.collection('competitions').doc(competitionId).collection('lead').doc('config');
}

/** Legacy path used for auto-migration of bk2026 */
const LEGACY_DOC = () => db.collection('lead-competition').doc('bk2026');

function mergeWithDefaults(raw: Record<string, unknown>): LeadCompetitionData {
  const rawSetup = (raw.setup && typeof raw.setup === 'object' ? raw.setup : {}) as Record<string, unknown>;

  const groups: LeadGroup[]  = Array.isArray(rawSetup.groups)  ? rawSetup.groups  as LeadGroup[]  : [];
  // Use stored classes; empty array for brand-new competitions
  const classes: LeadClass[] = Array.isArray(rawSetup.classes) ? rawSetup.classes as LeadClass[] : [];
  const locked: boolean      = typeof rawSetup.locked === 'boolean' ? rawSetup.locked : false;

  // Use stored athletes; empty array for brand-new competitions
  const athletes: LeadAthlete[] = Array.isArray(raw.athletes)
    ? raw.athletes as LeadAthlete[]
    : [];

  const scores: Record<string, LeadScore> =
    (raw.scores && typeof raw.scores === 'object' && !Array.isArray(raw.scores))
      ? raw.scores as Record<string, LeadScore>
      : {};

  return { setup: { groups, classes, locked }, athletes, scores };
}

function emptyData(): LeadCompetitionData {
  return { setup: { groups: [], classes: getDefaultData().setup.classes, locked: false }, athletes: [], scores: {} };
}

export async function loadLeadData(competitionId: string): Promise<LeadCompetitionData> {
  const ref = docRef(competitionId);
  let snap = await ref.get();

  if (!snap.exists && competitionId === 'bk2026') {
    const legacySnap = await LEGACY_DOC().get();
    if (legacySnap.exists) {
      await ref.set(legacySnap.data()!);
      snap = await ref.get();
    }
  }

  if (snap.exists) {
    return mergeWithDefaults(snap.data() as Record<string, unknown>);
  }
  const empty = emptyData();
  await ref.set(empty);
  return empty;
}

export async function saveLeadData(
  competitionId: string,
  data: LeadCompetitionData,
): Promise<void> {
  // JSON round-trip strips undefined fields which Firestore rejects
  const clean = JSON.parse(JSON.stringify(data));
  await docRef(competitionId).set(clean);
}

/**
 * Atomically update a single score entry.
 * Safe for concurrent jury members — only touches one field path.
 */
export async function saveScoreEntry(
  competitionId: string,
  athleteId: string,
  routeId: RouteId,
  score: LeadScore,
): Promise<void> {
  const key = scoreKey(athleteId, routeId);
  await docRef(competitionId).update({ [`scores.${key}`]: score });
}

export function subscribeLeadData(
  competitionId: string,
  onData: (data: LeadCompetitionData) => void,
  onError?: (err: Error) => void,
): () => void {
  return docRef(competitionId).onSnapshot(
    snap => {
      onData(snap.exists
        ? mergeWithDefaults(snap.data() as Record<string, unknown>)
        : emptyData()
      );
    },
    err => {
      console.error('Firestore lead-competition listener error:', err);
      onError?.(err);
    },
  );
}
